import type { UserSummary } from './user';

/**
 * A person in an assignment list, with how many cases they hold now. Sections 7
 * and 8 of the request of 5 October 2026: whoever assigns a case, or sends one
 * for rework, sees each technician's workload beside their name, so the work
 * goes to whoever has least.
 *
 * `activeCases` is the person's queue: the cases assigned to them that are still
 * in the lab. The server counts it; a response from a build that does not send
 * it reads as none.
 */
export interface Assignee extends UserSummary {
  activeCases: number;
}

/** How the people in an assignment list are ordered. */
export const ASSIGNEE_ORDERS = ['usual', 'leastBusy'] as const;
export type AssigneeOrder = (typeof ASSIGNEE_ORDERS)[number];

export function isAssigneeOrder(value: string | null | undefined): value is AssigneeOrder {
  return (ASSIGNEE_ORDERS as readonly string[]).includes(value ?? '');
}

/**
 * The people, in the order asked for. `usual` is the order the server sends:
 * whoever works the department first, then everybody else, each by name.
 * `leastBusy` is fewest cases first, and by name among people with the same
 * number, so the list does not shuffle between two refreshes.
 */
export function orderAssignees<Person extends { name: string; activeCases?: number }>(
  people: readonly Person[],
  order: AssigneeOrder,
): Person[] {
  if (order === 'usual') return [...people];
  return [...people].sort(
    (a, b) => (a.activeCases ?? 0) - (b.activeCases ?? 0) || a.name.localeCompare(b.name),
  );
}

/** Name search, as typed: anywhere in the name, without regard to case. */
export function matchesPerson(query: string, person: { name: string }): boolean {
  const needle = query.trim().toLowerCase();
  return needle === '' || person.name.toLowerCase().includes(needle);
}
